// @perlab/cross-tab — Replay buffer with type classification.
//
// Verbatim per spec §3.2.5 (28_Parallel_Work_Architecture_Tier1_V3.md):
//
//   SNAPSHOT_TYPES = Set of types whose latest payload should be replayed
//                    to late-joining tabs. ONLY these write to the replay
//                    store; ONLY these are eligible for reconcileOnMount.
//
//   EVENT_TYPES    = Set of types that are point-in-time events. NEVER
//                    written to replay; reconcileOnMount no-ops for them
//                    (V15 unit test asserts this).
//
//   isSnapshot(type) — fail-safe: unclassified types log a warn + return
//                      false (default to event semantics; do NOT replay
//                      an unknown type).
//
// Replay storage: one row per snapshot type, key = type, value = { env }.
// Latest-wins via overwrite. 30s staleness window — older entries are
// ignored on read (don't apply old themes to a tab that's been closed for
// an hour).

import type { BusEnvelope } from './envelope.js';
import type { KVStore } from './kv-store.js';

/**
 * Snapshot types — last value matters; replay on mount. VERBATIM per
 * spec §3.2.5. Slice 1 active subset is `theme.changed`; the rest are
 * declared here so the allowlist is complete from day 1 (no later
 * silent additions can sneak past the unclassified-warn fail-safe).
 */
export const SNAPSHOT_TYPES: ReadonlySet<string> = new Set([
  'theme.changed',
  'sidebar.changed',
  'pins.changed',
  'dnd.changed',
  'pinned-contexts.changed',
]);

/**
 * Event types — point-in-time signals. VERBATIM per spec §3.2.5.
 * Includes notification.* (per spec, these reconcile from
 * NotificationStore's own IDB, NOT from the replay buffer).
 */
export const EVENT_TYPES: ReadonlySet<string> = new Set([
  'notification',
  'notification.read',
  'notification.dismissed',
  'route.changed',
  'tab.hello',
  'tab.bye',
  'leader.elected',
  'dnd.local.changed',
]);

/**
 * Object-store name in the injected KVStore that holds replay rows.
 * Matches @perlab/cross-tab-idb's STORE_REPLAY constant by string value.
 */
const REPLAY_STORE = 'replay' as const;

/**
 * Spec §3.2.5: "if Date.now() - last.env.ts > REPLAY_MAX_AGE_MS return".
 * 30s window — long enough for the common "open second tab right after
 * toggling theme in first tab" flow, short enough that a tab returning
 * from sleep doesn't get hit with an hour-old snapshot.
 */
export const REPLAY_MAX_AGE_MS = 30_000;

interface ReplayRow {
  env: BusEnvelope;
}

/**
 * Per §3.2.5 — fail-safe classification:
 *   SNAPSHOT_TYPES → true (replay)
 *   EVENT_TYPES    → false (do NOT replay)
 *   anything else  → warn + false (default to event semantics)
 *
 * "When in doubt, do NOT replay" — replaying an unknown type risks applying
 * stale state to a component that doesn't expect it.
 */
export function isSnapshot(type: string): boolean {
  if (SNAPSHOT_TYPES.has(type)) return true;
  if (EVENT_TYPES.has(type)) return false;
  // eslint-disable-next-line no-console
  console.warn(`[cross-tab] unclassified type ${type} — defaulting to event (no replay)`);
  return false;
}

/**
 * Persist an envelope to the replay store IF its type is a snapshot type.
 * Called by the bus publish path after the network broadcast. No-op for
 * event types — they never write to replay.
 *
 * Intentionally tolerates KVStore errors (storage quota, transient IDB
 * failures): replay is a nice-to-have for late joiners, not load-bearing
 * for the publish path.
 */
export async function persistSnapshot(
  kv: KVStore,
  env: BusEnvelope
): Promise<void> {
  if (!isSnapshot(env.type)) return;
  try {
    await kv.put<ReplayRow>(REPLAY_STORE, env.type, { env });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[cross-tab] replay persist failed for ${env.type}`, err);
  }
}

/**
 * Per §3.2.5 — on mount, fetch the last snapshot for `type` and invoke
 * applyRemote if-and-only-if:
 *   1. `type` is classified as a snapshot (event types no-op).
 *   2. A row exists.
 *   3. Its originating tabId differs from `localTabId` (don't replay our
 *      own previous-session snapshots).
 *   4. Its `ts` is within REPLAY_MAX_AGE_MS.
 *
 * Returns true if applyRemote was invoked, false otherwise (useful for
 * test assertions like V15).
 */
export async function reconcileOnMount<T = unknown>(
  kv: KVStore,
  type: string,
  localTabId: string,
  applyRemote: (env: BusEnvelope<T>) => void
): Promise<boolean> {
  // VERBATIM PER SPEC: events explicitly no-op. The V15 test relies on this
  // exact early-return — do NOT invoke applyRemote for event types even if a
  // row somehow exists in the store.
  if (!isSnapshot(type)) return false;
  const row = await kv.get<ReplayRow>(REPLAY_STORE, type);
  if (!row || !row.env) return false;
  if (row.env.tabId === localTabId) return false;
  if (Date.now() - row.env.ts > REPLAY_MAX_AGE_MS) return false;
  applyRemote(row.env as BusEnvelope<T>);
  return true;
}
